import { useEffect, useLayoutEffect, useState } from 'react';
import { useI18n } from '../i18n';
import { Icon, SeverityIcon } from '../ui/Icon';

export interface ToastMessage {
  id: number;
  text: string;
  tone: 'success' | 'warning' | 'error' | 'info';
}

const TOAST_MS = 3000;

/**
 * One toast at a time, just under the top bar, auto-dismissed after 3 s; Escape or the close button
 * dismiss it. Side by side (desktop), it is centred over the manuscript column, so it never covers the
 * notebook's navigation; stacked (tablet, phone), over the page.
 */
export function Toast({ toast, onDone }: { toast: ToastMessage | null; onDone: () => void }) {
  const { t } = useI18n();
  const [place, setPlace] = useState<{ top: number; left: number | null; width: number | null }>({ top: 64, left: null, width: null });

  useLayoutEffect(() => {
    if (!toast) return;
    const header = document.querySelector('[data-app-header]');
    const top = Math.max(8, (header?.getBoundingClientRect().bottom ?? 56) + 8);
    const anchor = document.querySelector('[data-toast-anchor]')?.getBoundingClientRect();
    const sideBySide = anchor && anchor.width > 0 && anchor.width < window.innerWidth - 40 && anchor.top < top + 80;
    setPlace(sideBySide ? { top, left: anchor.left, width: anchor.width } : { top, left: null, width: null });
  }, [toast]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(onDone, TOAST_MS);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('dialog[open]')) onDone();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('keydown', onKey);
    };
  }, [toast, onDone]);

  return (
    <div
      aria-live="polite"
      role="status"
      className={`pointer-events-none fixed z-50 flex justify-center px-4 ${place.left === null ? 'inset-x-0' : ''}`}
      style={place.left === null ? { top: place.top } : { top: place.top, left: place.left, width: place.width ?? undefined }}
    >
      {toast && (
        <div
          key={toast.id}
          data-testid="toast"
          className="fl-toast pointer-events-auto flex max-w-[22rem] items-start gap-2 rounded-card border border-line bg-surface py-2 pl-3 pr-1.5 text-xs text-fg shadow-soft-lg"
        >
          {toast.tone === 'success' ? (
            <Icon name="check" size={13} className="mt-px text-accent" />
          ) : (
            <SeverityIcon severity={toast.tone === 'error' ? 'error' : toast.tone === 'warning' ? 'warning' : 'info'} size={13} className="mt-px" />
          )}
          <span className="min-w-0 flex-1 leading-snug">{toast.text}</span>
          <button
            type="button"
            onClick={onDone}
            aria-label={t('toast.dismiss')}
            title={t('toast.dismissTitle')}
            className="-my-0.5 rounded-pill p-1 text-subtle hover:bg-fg/10 hover:text-fg"
            data-testid="toast-dismiss"
          >
            <Icon name="x" size={10} />
          </button>
        </div>
      )}
    </div>
  );
}
